import * as React from 'react';
import { ChevronDown, History } from 'lucide-react';
import { useHistoricoInventario } from '@/hooks/inventario';
import { cn } from '@/lib/utils';
import { Cartao, Esqueleto } from '@/componentes/ui/primitivos';

/**
 * O histórico do inventário: quem criou, congelou, fechou, desfez, descongelou
 * ou excluiu, e quando — da auditoria do WMS. É aqui que fica o rastro de um
 * escopo que foi excluído (ele some do quadro, o registro não).
 */

const ROTULO: Record<string, string> = {
  'inventario.criar': 'Criou o inventário',
  'inventario.atualizar': 'Alterou o inventário',
  'inventario.escopo.criar': 'Criou escopo',
  'inventario.escopo.produtos': 'Reinseriu produtos',
  'inventario.escopo.congelar': 'Congelou o estoque',
  'inventario.escopo.descongelar': 'Descongelou',
  'inventario.escopo.excluir': 'Excluiu escopo',
  'inventario.escopo.fechar_contagem': 'Fechou a 1ª contagem',
  'inventario.escopo.fechar_recontagem': 'Fechou a recontagem',
  'inventario.escopo.fechar_final': 'Fechou a contagem final',
  'inventario.escopo.fechar_integracao': 'Integrou ao estoque',
  'inventario.escopo.desfazer_contagem': 'Desfez a 1ª contagem',
  'inventario.escopo.desfazer_recontagem': 'Desfez a recontagem',
  'inventario.escopo.desfazer_final': 'Desfez a contagem final',
  'inventario.escopo.desfazer_integracao': 'Estornou a integração',
};

const PERIGO = /desfazer|descongelar|excluir/;

function quando(v: string): string {
  return `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)} ${v.slice(11, 16)}`;
}

/** Tira o "#id" do começo e as aspas que a auditoria grava. */
function limpar(detalhe: string | null): string {
  return (detalhe ?? '').replace(/^"|"$/g, '').replace(/^#\d+:?\s*/, '');
}

export function HistoricoInventario({ inventario }: { inventario: number }): JSX.Element {
  const [aberto, definirAberto] = React.useState(false);
  const consulta = useHistoricoInventario(inventario);
  const lista = consulta.data?.historico ?? [];

  return (
    <Cartao className="overflow-hidden p-0">
      <button
        type="button"
        onClick={() => definirAberto((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold hover:bg-superficie2"
      >
        <History className="h-4 w-4 text-textoFraco" />
        Histórico de ações
        <span className="text-xs font-normal text-textoFraco">
          {consulta.data ? `${lista.length} registro${lista.length === 1 ? '' : 's'}` : ''}
        </span>
        <ChevronDown className={cn('ml-auto h-4 w-4 transition-transform', aberto && 'rotate-180')} />
      </button>
      {aberto ? (
        consulta.isLoading ? (
          <div className="p-4">
            <Esqueleto className="h-24" />
          </div>
        ) : lista.length === 0 ? (
          <p className="border-t border-borda px-4 py-4 text-sm text-textoSuave">
            Nenhuma ação registrada ainda.
          </p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto border-t border-borda">
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 bg-superficie2 text-left text-[10.5px] uppercase tracking-wide text-textoFraco">
                <tr>
                  <th className="px-4 py-2 font-semibold">Quando</th>
                  <th className="px-2 py-2 font-semibold">Quem</th>
                  <th className="px-2 py-2 font-semibold">Ação</th>
                  <th className="px-2 py-2 font-semibold">Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((h, i) => (
                  <tr key={`${h.dataHora}-${i}`} className="border-t border-borda align-top">
                    <td className="tabular whitespace-nowrap px-4 py-1.5 text-textoFraco">
                      {quando(h.dataHora)}
                    </td>
                    <td className="px-2 py-1.5">{h.nome ?? h.login ?? '—'}</td>
                    <td
                      className={cn(
                        'whitespace-nowrap px-2 py-1.5 font-medium',
                        PERIGO.test(h.acao) && 'text-status-divergencia',
                        h.resultado !== 'sucesso' && 'text-textoSuave line-through',
                      )}
                    >
                      {ROTULO[h.acao] ?? h.acao.replace(/^inventario\./, '')}
                      {h.resultado !== 'sucesso' ? (
                        <span className="ml-1 text-[10.5px] no-underline">(recusado)</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 text-textoFraco">{limpar(h.detalhe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </Cartao>
  );
}
